import { useState, useEffect, useCallback } from 'react';
import type { MediaItem } from '@/types';
import { api } from '@/lib/api';
import VideoPreview from '@/components/VideoPreview';

export default function MediaLibrary() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'image' | 'video'>('all');
  const [search, setSearch] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.media.list({
        type: filter === 'all' ? undefined : filter,
        limit: 100,
      });
      setMedia(result.items);
    } catch {
      // Use defaults
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const filteredMedia = media.filter((item) =>
    search ? item.key.toLowerCase().includes(search.toLowerCase()) : true,
  );

  const handleDelete = async (key: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    setDeleting(key);
    try {
      await api.media.delete(key);
      setMedia((prev) => prev.filter((m) => m.key !== key));
      if (selectedMedia?.key === key) setSelectedMedia(null);
    } catch {
      alert('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Media Library</h2>
        <p className="text-muted-foreground">Manage your product images and videos</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {(['all', 'image', 'video'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === type ? 'bg-primary-600 text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="card text-center">
          <p className="text-muted-foreground">No media files found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filteredMedia.map((item) => (
            <div
              key={item.key}
              className="group relative cursor-pointer overflow-hidden rounded-lg border border-border transition-shadow hover:shadow-md"
              onClick={() => setSelectedMedia(item)}
            >
              <div className="aspect-square bg-muted">
                {item.type === 'image' ? (
                  <img src={item.url || item.thumbnailUrl} alt={item.key} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-black/10">
                    <svg className="h-12 w-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.key);
                  }}
                  disabled={deleting === item.key}
                  className="rounded bg-destructive p-2 text-white hover:bg-destructive/90"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                <p className="truncate text-xs text-white">{item.key}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-surface p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">File Details</h3>
              <button onClick={() => setSelectedMedia(null)} className="rounded p-1 hover:bg-accent">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedMedia.type === 'video' ? (
              <VideoPreview media={selectedMedia} />
            ) : (
              <img src={selectedMedia.url} alt="" className="mb-4 w-full rounded-lg" />
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Filename</p>
                <p className="font-medium text-foreground">{selectedMedia.key}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium text-foreground capitalize">{selectedMedia.type}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Size</p>
                <p className="font-medium text-foreground">{formatSize(selectedMedia.size)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Uploaded</p>
                <p className="font-medium text-foreground">{formatDate(selectedMedia.uploadedAt)}</p>
              </div>
              {selectedMedia.width && selectedMedia.height && (
                <div>
                  <p className="text-muted-foreground">Dimensions</p>
                  <p className="font-medium text-foreground">{selectedMedia.width}x{selectedMedia.height}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">MIME Type</p>
                <p className="font-medium text-foreground">{selectedMedia.mimeType}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => handleDelete(selectedMedia.key)}
                className="btn-destructive"
              >
                Delete
              </button>
              <button type="button" onClick={() => setSelectedMedia(null)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
